import React from 'react';
import PropTypes from 'prop-types';
import {CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE} from '../../../../constants/cabinetInteriorConstants';

import {
  ALIGN,
  canSectionHaveDoor,
  sumSectionHeights,
} from './helpers/cabinetInteriorLayoutHelper';
import { iconLink } from '../../../../assetsSQFT';

const CabinetDoorExtensionPreview = ({
  layout,
  interiorHeight,
  isDoorExtensionEnabled,
  onBoundaryClick,
}) => {
  const sections = layout ? layout.sections : [];
  const totalSectionHeight = layout ? sumSectionHeights(layout.sections) : 0;
  const freeHeight = Math.max(0, interiorHeight - totalSectionHeight);
  const freeRow = freeHeight > 0
    ? [{ id: 'free-space', height: freeHeight, isFreeSpace: true }]
    : [];
  const rows = layout && layout.alignment === ALIGN.bottom
    ? [...freeRow, ...sections]
    : [...sections, ...freeRow];
  const canLinkSections = (leftSection, rightSection) => (
    isDoorExtensionEnabled
    && leftSection
    && rightSection
    && leftSection.type === SECTION_TYPE.box
    && rightSection.type === SECTION_TYPE.box
  );

  return (
    <div className="tci-preview">
      <div className="tci-preview__cabinet">
        {rows.map((section) => {
          const isActiveDoorSection = (
            isDoorExtensionEnabled
            && !section.isFreeSpace
            && section.type === SECTION_TYPE.box
            && section.hasDoor !== false
            && section.hasDoorExtension
          );
          const sectionIndex = sections.indexOf(section);
          const previousSection = sectionIndex > 0 ? sections[sectionIndex - 1] : null;
          const nextSection = sectionIndex >= 0 ? sections[sectionIndex + 1] : null;
          const groupId = section.doorExtensionGroupId;
          const previousGroupId = previousSection && previousSection.doorExtensionGroupId;
          const nextGroupId = nextSection && nextSection.doorExtensionGroupId;
          const isActiveStart = isActiveDoorSection && groupId !== previousGroupId;
          const isActiveEnd = isActiveDoorSection && groupId !== nextGroupId;
          const canLinkToPrevious = canLinkSections(previousSection, section);
          const canLinkToNext = canLinkSections(section, nextSection);
          const hasConnector = canLinkToPrevious || canLinkToNext;
          const isLinkedToPrevious = Boolean(
            canLinkToPrevious
            && groupId
            && groupId === previousGroupId
          );
          const isLinkedToNext = Boolean(
            canLinkToNext
            && groupId
            && groupId === nextGroupId
          );
          const hasActiveConnector = isLinkedToPrevious || isLinkedToNext;
          const hasUnifiedVerticalConnector = (
            canLinkToPrevious
            && canLinkToNext
            && isLinkedToPrevious === isLinkedToNext
          );
          const shouldRenderUpConnector = canLinkToPrevious && !hasUnifiedVerticalConnector;
          const shouldRenderDownConnector = canLinkToNext && !hasUnifiedVerticalConnector;
          const rowHeightPercent = interiorHeight > 0
            ? (section.height / interiorHeight) * 100
            : 0;
          const sectionLabel = sectionIndex >= 0 ? String.fromCharCode(65 + sectionIndex) : '';

          return (
            <div
              key={section.id}
              className={'tci-preview__row'
                + (section.isFreeSpace ? ' tci-preview__row--free' : '')
                + (isActiveDoorSection ? ' tci-preview__row--active' : '')
                + (isActiveStart ? ' tci-preview__row--active-start' : '')
                + (isActiveEnd ? ' tci-preview__row--active-end' : '')
                + (hasConnector ? ' tci-preview__row--connector' : '')
                + (canLinkToPrevious ? ' tci-preview__row--connector-up' : '')
                + (canLinkToNext ? ' tci-preview__row--connector-down' : '')}
              style={{ height: `${rowHeightPercent}%` }}
            >
              {hasConnector ? (
                <span
                  className={'tci-preview__connector-horizontal'
                    + (hasActiveConnector ? ' tci-preview__connector-horizontal--active' : '')}
                />
              ) : null}
              {hasUnifiedVerticalConnector ? (
                <span
                  className={'tci-preview__connector-vertical tci-preview__connector-vertical--full'
                    + (isLinkedToPrevious ? ' tci-preview__connector-vertical--active' : '')}
                />
              ) : null}
              {shouldRenderUpConnector ? (
                <span
                  className={'tci-preview__connector-vertical tci-preview__connector-vertical--up'
                    + (isLinkedToPrevious ? ' tci-preview__connector-vertical--active' : '')}
                />
              ) : null}
              {shouldRenderDownConnector ? (
                <span
                  className={'tci-preview__connector-vertical tci-preview__connector-vertical--down'
                    + (isLinkedToNext ? ' tci-preview__connector-vertical--active' : '')}
                />
              ) : null}
              <span className="tci-preview__label">{sectionLabel}</span>
              <div
                className={'tci-preview__section'
                  + (isActiveDoorSection ? ' tci-preview__section--active' : '')
                  + (canSectionHaveDoor(section.type) && section.hasDoor === false ? ' tci-preview__section--no-door' : '')
                  + (!isActiveDoorSection && !section.isFreeSpace ? ' tci-preview__section--neutral' : '')}
              />
              {canLinkToNext ? (
                <span
                  className={'tci-preview__link'
                    + (isLinkedToNext ? ' tci-preview__link--active' : '')}
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    onBoundaryClick(section.id, nextSection.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      onBoundaryClick(section.id, nextSection.id);
                    }
                  }}
                >
                  <span className="tci-preview__link-rail" />
                  <img src={iconLink} alt="" className="tci-preview__link-icon" />
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

CabinetDoorExtensionPreview.propTypes = {
  layout: PropTypes.shape({
    alignment: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      height: PropTypes.number.isRequired,
      hasDoor: PropTypes.bool,
      hasDoorExtension: PropTypes.bool,
      doorExtensionGroupId: PropTypes.string,
    })),
  }),
  interiorHeight: PropTypes.number.isRequired,
  isDoorExtensionEnabled: PropTypes.bool.isRequired,
  onBoundaryClick: PropTypes.func,
};

CabinetDoorExtensionPreview.defaultProps = {
  layout: null,
  onBoundaryClick: () => {},
};

export default CabinetDoorExtensionPreview;
